/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale Vale.Spelling = NO -->*/
/*Allow usage of "Dest." to keep table formatting nice*/
/*<!-- vale Vale.Spelling = YES -->*/
/*<!-- vale Google.Headings = NO -->*/
/*Allow non-sentence-case headings to match in-app capitalization*/
/*<!-- vale Google.Headings = YES -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Role-Based access control", description = "Hightouch provides out-of-the-box role-based access control (RBAC) governing what actions a user can take on workspace resources.", lastUpdatedDate = new Date(1726531200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Role-Based access control",
        description: "Hightouch provides out-of-the-box role-based access control (RBAC) governing what actions a user can take on workspace resources.",
        lastUpdatedDate: "Tue Sep 17 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "What is a role?",
          "depth": 0,
          "id": "what-is-a-role"
        }, {
          "value": "Default roles",
          "depth": 0,
          "id": "default-roles"
        }, {
          "value": "Using roles with approval flows",
          "depth": 1,
          "id": "using-roles-with-approval-flows"
        }, {
          "value": "Custom roles",
          "depth": 0,
          "id": "custom-roles"
        }, {
          "value": "Customizing roles",
          "depth": 1,
          "id": "customizing-roles"
        }, {
          "value": "Default role policies",
          "depth": 0,
          "id": "default-role-policies"
        }, {
          "value": "Admin",
          "depth": 1,
          "id": "admin"
        }, {
          "value": "Workspace editor",
          "depth": 1,
          "id": "workspace-editor"
        }, {
          "value": "Model and sync editor",
          "depth": 1,
          "id": "model-and-sync-editor"
        }, {
          "value": "Sync editor",
          "depth": 1,
          "id": "sync-editor"
        }, {
          "value": "Audience editor",
          "depth": 1,
          "id": "audience-editor"
        }, {
          "value": "Source admin",
          "depth": 1,
          "id": "source-admin"
        }, {
          "value": "Destination admin",
          "depth": 1,
          "id": "destination-admin"
        }, {
          "value": "Workspace viewer",
          "depth": 1,
          "id": "workspace-viewer"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      strong: "strong",
      a: "a",
      h2: "h2",
      ul: "ul",
      li: "li",
      code: "code",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      em: "em",
      h3: "h3",
      img: "img",
      h4: "h4",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Role-based access control"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch provides out-of-the-box ", _jsxs(_components.strong, {
          children: ["role-based access control ", _jsx(_components.a, {
            href: "https://en.wikipedia.org/wiki/Role-based_access_control",
            children: "(RBAC)"
          })]
        }), " to govern what actions a user can take on workspace resources. All accounts come with the default Admin role. Business-tier customers can access the additional ", _jsx(_components.a, {
          href: "#default-roles",
          children: "default roles"
        }), " and create ", _jsx(_components.a, {
          href: "#custom-roles",
          children: "custom roles"
        }), " as needed."]
      }), "\n", _jsx(_components.p, {
        children: "Users can be only assigned one role. When a user doesn't have sufficient privileges to a resource, the resource is grayed out in the UI."
      }), "\n", _jsx(_components.h2, {
        id: "what-is-a-role",
        children: "What is a role?"
      }), "\n", _jsxs(_components.p, {
        children: ["A role consists of a set of ", _jsx(_components.strong, {
          children: "policies"
        }), " defining access to resources in the workspace. A policy is a set of actions, resources, and conditions."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#actions",
            children: _jsx(_components.strong, {
              children: "Actions"
            })
          }), " represent the operations that specific roles can perform, for example, ", _jsx(_components.code, {
            children: "create"
          }), ", ", _jsx(_components.code, {
            children: "read"
          }), ", ", _jsx(_components.code, {
            children: "update"
          }), ", ", _jsx(_components.code, {
            children: "delete"
          }), ", etc."]
        }), "\n", _jsxs(_components.li, {
          children: ["Actions are performed on ", _jsx(_components.a, {
            href: "#resources",
            children: _jsx(_components.strong, {
              children: "resources"
            })
          }), "—sources, destinations, models, syncs, audiences, account management features, etc."]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) ", _jsx(_components.strong, {
            children: "Conditions"
          }), " determine how policies are applied, for example, you may restrict a policy to resources ", _jsx(_components.a, {
            href: "/workspace-management/lbac",
            children: "labeled"
          }), " ", _jsx(_components.code, {
            children: "project:marketing"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "default-roles",
        children: "Default roles"
      }), "\n", _jsxs(_components.p, {
        children: ["All Hightouch workspaces come with the Admin role. These additional predefined roles are available on ", _jsx(_components.a, {
          href: "/pricing/bt-pricing",
          children: "Business tier plans"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Admin"
          }), ": This role has unrestricted access to all resources in the workspace. Admins can also manage the workspace itself, including managing user membership and roles, creating new API keys, setting up custom storage, and configuring extensions with external services like Slack and PagerDuty."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Workspace editor"
          }), ": This role is for advanced business users who need full access to the workspace except for workspace settings, such as user membership and roles. The role has full read and write access to core resources in the workspace. This includes sources, destinations, models, audiences, and syncs. However, the role can't perform any workspace management."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Model + sync editor"
          }), ": This role is for users, such as business analysts, who regularly manage syncs and modify data. The role has read and write access to models, audiences, and syncs, but can't edit sources and destinations."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync editor"
          }), ": This role is for users, such as growth marketers, who regularly manage syncs, but don't need to modify data. The role has read and write access to syncs (including enabling syncs), but can't edit sources, destinations, and models."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Audience editor"
          }), ": This role is for users, such as lifecycle marketers, who regularly work with audiences. The role has read and write access to audiences and syncs, but can't edit sources, destinations, models, parent models, and non-audience syncs. It also can't enable syncs or trigger sync runs."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Source admin"
          }), ": This role is for users, such as data analysts, who need to set up data systems. The role has read and write access to sources and models, but can't edit destinations and syncs."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Destination admin:"
          }), " This role is for users, such as SaaS administrators, who need to set up downstream business systems. The role has read and write access to destinations, but can't edit sources, models, and syncs."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Workspace viewer"
          }), ": This role has read-only access to core resources in the workspace. This includes sources, destinations, models, audiences, and syncs. The role can't manage the workspace itself, such as changing workspace configurations."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Workspace draft contributor"
          }), ": This role is similar to a Workspace editor, except that they can edit sources. However, any edits they make to sources or models require approval before they can go live. Read about ", _jsx(_components.a, {
            href: "/workspace-management/approval-flows",
            children: "approval flows"
          }), " to learn more."]
        }), "\n"]
      }), "\n", "\n", "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {
              children: "Source"
            }), _jsx(_components.th, {
              children: "Model"
            }), _jsx(_components.th, {
              children: "Dest."
            }), _jsx(_components.th, {
              children: "Sync"
            }), _jsx(_components.th, {
              children: "Audience"
            }), _jsx(_components.th, {
              children: "Account"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Admin"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "Full"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Workspace editor"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "No Access"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Model + sync editor"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "No Access"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Sync editor"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "No Access"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Audience editor"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Limited"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "No Access"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Source admin"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "No Access"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Destination admin"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Full"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Workspace viewer"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "Read"
            }), _jsx(_components.td, {
              children: "No Access"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Workspace draft contributor"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "Full*"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "Full*"
            }), _jsx(_components.td, {
              children: "Full"
            }), _jsx(_components.td, {
              children: "No Access"
            })]
          })]
        })]
      }), "\n", "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.em, {
            children: "Full"
          }), " permission indicates that this role can edit resources without approval."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.em, {
            children: "Full*"
          }), " permission indicates that this role can edit resources but ", _jsx(_components.a, {
            href: "/workspace-management/approval-flows",
            children: "require approval"
          }), " before changes can go live."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.em, {
            children: "Limited"
          }), " permission indicates that this role has read and write access to view and edit syncs, but it can't enable syncs or trigger sync runs."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "#default-role-policies",
          children: "default role policies"
        }), " for a JSON description of each role."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The ", _jsx(_components.strong, {
            children: "Workspace editor"
          }), " and ", _jsx(_components.strong, {
            children: "Workspace viewer"
          }), " roles were previously named ", _jsx(_components.strong, {
            children: "Editor"
          }), " and ", _jsx(_components.strong, {
            children: "Viewer"
          }), ", respectively. You may noticed ", _jsx(_components.strong, {
            children: "Editor"
          }), " and ", _jsx(_components.strong, {
            children: "Viewer"
          }), " roles in workspaces created before September 2022."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "using-roles-with-approval-flows",
        children: "Using roles with approval flows"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/workspace-management/approval-flows",
          children: "Approval flows"
        }), " let Business tier customers stage new resources (models and syncs) and changes to existing resources in ", _jsx(_components.strong, {
          children: "draft mode"
        }), " until an approver reviews them. Only Workspace Draft Contributors' changes require approval and all roles except Workspace Draft Contributor and Workspace Viewer can grant approval."]
      }), "\n", _jsx(_components.p, {
        children: "If you use approval flows, we recommend using the following roles only:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Admin - can grant approvals and handle account management"
        }), "\n", _jsx(_components.li, {
          children: "Workspace editor - can grant approvals"
        }), "\n", _jsx(_components.li, {
          children: "Workspace draft contributor - can author changes but requires approval"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To learn more about how approval flows work, read the ", _jsx(_components.a, {
          href: "/workspace-management/approval-flows",
          children: "approval flows documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "custom-roles",
        children: "Custom roles"
      }), "\n", _jsxs(_components.p, {
        children: ["For advanced Business tier customers, Hightouch exposes a custom role builder for workspace owners to create their own roles. Custom roles can reference resource labels, and are often used with ", _jsx(_components.a, {
          href: "/workspace-management/lbac",
          children: _jsx(_components.strong, {
            children: "label-based access control (LBAC)"
          })
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "LBAC combined with custom roles is geared for workspaces with especially sensitive data sets, companies that want finer control of their production syncs, or auditors who want to ensure that SOC2 compliance is maintained."
      }), "\n", _jsx(_components.h3, {
        id: "customizing-roles",
        children: "Customizing roles"
      }), "\n", _jsxs(_components.p, {
        children: ["To create a custom role in your workspace, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/settings/roles",
          children: [_jsx(_components.strong, {
            children: "Roles"
          }), " tab"]
        }), " in the ", _jsx(_components.strong, {
          children: "Settings"
        }), " page."]
      }), "\n", _jsxs(_components.p, {
        children: ["Once there, you can create a new role by clicking on ", _jsx(_components.strong, {
          children: "Add Role"
        }), ". You can also edit existing custom roles by clicking on the role name."]
      }), "\n", _jsxs(_components.p, {
        children: ["In the modal that appears, enter the ", _jsx(_components.strong, {
          children: "Name"
        }), " for the custom role."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can then choose to create role by toggling on the ", _jsx(_components.a, {
          href: "#json-builder",
          children: _jsx(_components.strong, {
            children: "JSON Builder"
          })
        }), " and entering JSON or selecting the role's permissions via checkboxes. If using the UI role builder, don't forget to click ", _jsx(_components.strong, {
          children: "Add"
        }), " once you've selected all the role's permissions."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/rbac-ui-builder.png",
          alt: "Using the UI role builder to create a new Marketing role"
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["You can only add ", _jsx(_components.a, {
            href: "#what-is-a-role",
            children: "conditions"
          }), " using the JSON builder, not using the UI role builder."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "json-builder",
        children: "JSON builder"
      }), "\n", _jsxs(_components.p, {
        children: ["When using the JSON builder, recall that ", _jsx(_components.a, {
          href: "#what-is-a-role",
          children: "each role is made of an array of policy objects"
        }), " described in JSON."]
      }), "\n", _jsx(_components.p, {
        children: "Each policy consists of:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["An ", _jsx(_components.strong, {
            children: "effect"
          }), ": either ", _jsx(_components.code, {
            children: "allow"
          }), " or ", _jsx(_components.code, {
            children: "deny"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#actions",
            children: _jsx(_components.strong, {
              children: "Actions"
            })
          }), ": operations that users assigned to the role are able to complete"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#resources",
            children: _jsx(_components.strong, {
              children: "Resources"
            })
          }), ": resources that those actions apply to"]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) ", _jsx(_components.strong, {
            children: "Conditions"
          }), ": limitations on when to apply actions"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["This is an example of a custom role that allows full access to destinations, sources, models, and syncs that have been ", _jsx(_components.a, {
          href: "/workspace-management/lbac",
          children: "labeled"
        }), " ", _jsx(_components.code, {
          children: "project:marketing"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the label-based access control documentation to learn more about ", _jsx(_components.a, {
          href: "/workspace-management/lbac#what-are-conditions",
          children: "how to create roles with conditions"
        }), "."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"version\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2022-04-26\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"policies\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"actions\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"*\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"destination\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"source\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"model\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sync\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"conditions\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"labels.project\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n          ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"equals\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"marketing\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["By default, all permissions are set to ", _jsx(_components.code, {
            children: "deny"
          }), ". An empty policies array means\nthat all actions are denied."]
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Make sure to not edit the ", _jsx(_components.code, {
            children: "\"version\""
          }), " date when creating a custom role."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "actions",
        children: "Actions"
      }), "\n", _jsxs(_components.p, {
        children: ["Actions are operations that users are allowed to make. Hightouch supports permissions on basic ", _jsx(_components.a, {
          href: "https://en.wikipedia.org/wiki/Create,_read,_update_and_delete",
          children: "CRUD actions"
        }), " for all resources and a set of granular actions for specific resources."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Action"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Applicable Resources"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "create"
              })
            }), _jsx(_components.td, {
              children: "Permission to add new resources"
            }), _jsx(_components.td, {
              children: "All"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "read"
              })
            }), _jsxs(_components.td, {
              children: ["Permission to view ", _jsx(_components.em, {
                children: "and"
              }), " use existing resources"]
            }), _jsx(_components.td, {
              children: "All"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "update"
              })
            }), _jsx(_components.td, {
              children: "Permission to edit existing resources"
            }), _jsx(_components.td, {
              children: "All"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "delete"
              })
            }), _jsx(_components.td, {
              children: "Permission to delete existing resources"
            }), _jsx(_components.td, {
              children: "All"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "preview"
              })
            }), _jsx(_components.td, {
              children: "Permission to view results of a model"
            }), _jsx(_components.td, {
              children: "Sources and models"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "start"
              })
            }), _jsx(_components.td, {
              children: "Permission to trigger a manual sync"
            }), _jsx(_components.td, {
              children: "Sync only"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "enable"
              })
            }), _jsx(_components.td, {
              children: "Permission to trigger enable a sync"
            }), _jsx(_components.td, {
              children: "Sync only"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "debugger"
              })
            }), _jsx(_components.td, {
              children: "Permission to view results of an existing sync"
            }), _jsx(_components.td, {
              children: "Sync only"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "testrow"
              })
            }), _jsx(_components.td, {
              children: "Permission to test a row in a sync"
            }), _jsx(_components.td, {
              children: "Sync only"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "approve"
              })
            }), _jsx(_components.td, {
              children: "Permission to approve changes"
            }), _jsx(_components.td, {
              children: "Syncs and models"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "*"
              })
            }), _jsx(_components.td, {
              children: "Denotes all actions"
            }), _jsx(_components.td, {
              children: "All"
            })]
          })]
        })]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["The ", _jsx(_components.code, {
            children: "read"
          }), " permission on a resource is ", _jsx(_components.strong, {
            children: "required"
          }), " when another resource\nneeds to use it, similar to ", _jsx(_components.strong, {
            children: "access"
          }), " permissions in database systems. For\nexample, for a role to ", _jsx(_components.code, {
            children: "create"
          }), " syncs, the role also needs ", _jsx(_components.code, {
            children: "read"
          }), " permission\non sources and models."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "resources",
        children: "Resources"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Resource"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "workspace"
              })
            }), _jsx(_components.td, {
              children: "Catch-all for workspace-level features like extensions"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "workspace_membership"
              })
            }), _jsx(_components.td, {
              children: "Permission to invite users"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "source"
              })
            }), _jsx(_components.td, {
              children: "Access to sources"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "destination"
              })
            }), _jsx(_components.td, {
              children: "Access to destinations"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "model"
              })
            }), _jsx(_components.td, {
              children: "Access to models"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sync"
              })
            }), _jsx(_components.td, {
              children: "Access to syncs"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "alert"
              })
            }), _jsx(_components.td, {
              children: "Access to alerts on syncs"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "audience"
              })
            }), _jsx(_components.td, {
              children: "Access to individual audiences"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "audience_schema"
              })
            }), _jsx(_components.td, {
              children: "Access to audience setup, including parent models, event models, and related models"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sync_templates"
              })
            }), _jsx(_components.td, {
              children: "Access to sync templates"
            }), _jsx(_components.td, {})]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "default-role-policies",
        children: "Default role policies"
      }), "\n", "\n", "\n", _jsx(_components.h3, {
        id: "admin",
        children: "Admin"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"version\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2022-04-26\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"policies\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"actions\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"*\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"*\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "workspace-editor",
        children: "Workspace editor"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"version\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2022-04-26\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"policies\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"actions\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"*\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"destination\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"source\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"model\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sync\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"audience\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"audience_schema\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sync_template\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"workspace_membership\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"alert\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "model-and-sync-editor",
        children: "Model and sync editor"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"version\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2022-04-26\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"policies\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"actions\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"read\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"preview\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"source\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"destination\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"actions\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"*\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"model\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sync\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"audience\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"audience_schema\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sync_template\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"alert\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "sync-editor",
        children: "Sync editor"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"version\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2022-04-26\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"policies\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"actions\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"read\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"source\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"destination\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"model\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"actions\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"*\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sync\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"audience\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"audience_schema\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sync_template\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"alert\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "audience-editor",
        children: "Audience editor"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"version\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2022-04-26\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"policies\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"actions\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"read\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"source\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"destination\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"model\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"audience_schema\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sync_template\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"alert\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"actions\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"create\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"read\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"update\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sync\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"actions\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"*\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"audience\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "source-admin",
        children: "Source admin"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"version\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2022-04-26\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"policies\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"actions\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"*\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"source\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"model\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"actions\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"read\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"destination\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sync\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sync_template\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"audience\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"workspace_membership\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"workspace\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "destination-admin",
        children: "Destination admin"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"version\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2022-04-26\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"policies\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"actions\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"read\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"source\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"model\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"actions\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"*\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"destination\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sync\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"audience\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"audience_schema\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sync_template\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"alert\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"workspace_membership\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"workspace\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "workspace-viewer",
        children: "Workspace viewer"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"version\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2022-04-26\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"policies\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"actions\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"read\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"source\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"destination\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"model\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sync\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"audience\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"audience_schema\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sync_template\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"workspace_membership\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"alert\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n"]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
